/* eslint-disable max-lines */
import React, {useMemo} from "react";
import {Cell, Column, useExpanded, UseExpandedRowProps, useTable} from "react-table";
import {gql} from "@apollo/client";
import {
  ChallengeMode,
  EducationMemberRow,
  EducationMemberSortField,
  EducationMemberSortInput,
  EducationSessionRecordState,
  Participation,
} from "../../../schema";

import {formatDateTime, formatDuration, formatToMoscowDate} from "../../../time";
import {isUserAdmin} from "../../../types";
import {useTranslation} from "react-i18next";
import FeedbackWidget from "../../FeedbackWidget";
import {CaretDown, CaretForward, HelpCircleIcon, PlayIcon, CopyIcon} from "../../../ui/icons";
import MemberEditDropdown from "./MemberEditDropdown";
import ParticipationEditDropdown from "./ParticipationEditDropdown";
import UserItem from "../UserItem";

import cn from "classnames";
import classes from "./MembersTable.module.css";
import useVisibility from "../../../hooks/useVisibility";
import RecordLink from "../../RecordLink";
import Link from "../../../ui/link";
import FullscreenVideo from "../../../ui/fullscreenvideo";
import ReactLink from "../../ReactLink";
import {reverse} from "../../../routing";
import WithTooltip from "../../../ui/tooltip";
import SortButton from "../../../ui/sortbutton";
import getEmotionColumn from "./column/EmotionColumn";
import WithExpandableText from "../../../ui/expandabletext";
import tippy from "tippy.js";

type CellDataType = EducationMemberRow | Participation;
type MembersCell = Cell<CellDataType> & {
  row: UseExpandedRowProps<CellDataType>;
};

export type params = {
  data: EducationMemberRow[];
  singleSubRow: boolean | undefined;

  challenges: React.ComponentProps<typeof MemberEditDropdown>["challenges"];
  projects: React.ComponentProps<typeof MemberEditDropdown>["projects"];
  onAssign: React.ComponentProps<typeof MemberEditDropdown>["onAssign"];
  onSetStatus: React.ComponentProps<typeof MemberEditDropdown>["onSetStatus"];
  onUserBestAttemptsReportExport: React.ComponentProps<typeof MemberEditDropdown>["onUserBestAttemptsReportExport"];
  onSetParticipationStatus: React.ComponentProps<typeof ParticipationEditDropdown>["onSetStatus"];
  onParticipationDelete: React.ComponentProps<typeof ParticipationEditDropdown>["onParticipationDelete"];
  onParticipationReportExport: React.ComponentProps<typeof ParticipationEditDropdown>["onParticipationReportExport"];
  onSetSort: React.Dispatch<React.SetStateAction<EducationMemberSortInput>>;
  sort: EducationMemberSortInput;
  defaultSort: EducationMemberSortInput;
};

function useMembersTableTable(
  data: params["data"],
  singleSubRow: params["singleSubRow"],

  challenges: params["challenges"],
  projects: params["projects"],
  onAssign: params["onAssign"],
  onSetStatus: params["onSetStatus"],
  onSetParticipationStatus: params["onSetParticipationStatus"],
  onParticipationDelete: params["onParticipationDelete"],
  onUserBestAttemptsReportExport: params["onUserBestAttemptsReportExport"],
  onParticipationReportExport: params["onParticipationReportExport"],
  onSetSort: params["onSetSort"],
  sort: params["sort"],
  defaultSort: params["defaultSort"],
) {
  const {t, i18n} = useTranslation();

  const columns = useMemo<Column<CellDataType>[]>(() => {
    const sortProps = {
      onSetSort,
      defaultSort,
      selectedSort: sort,
    };
    return [
      {
        id: "expand",
        Header: () => null,
        Cell: ({row}: MembersCell) =>
          row.canExpand ? (
            <span {...row.getToggleRowExpandedProps()}>{row.isExpanded ? <CaretDown /> : <CaretForward />}</span>
          ) : null,
      },

      {
        id: "id",
        Header: () => (
          <div className={classes.withSort}>
            {t("components.MembersTable.user")}
            <SortButton field={EducationMemberSortField.EMAIL} {...sortProps} />
          </div>
        ),
        Cell: ({row}: MembersCell) => {
          switch (row.original.__typename) {
            case "EducationMemberRow":
              return (
                <UserItem
                  user={row.original.user}
                  isAdmin={isUserAdmin(row.original.user)}
                  {...row.getToggleRowExpandedProps()}
                />
              );
            case "Participation":
              if (row.original.challenge.projectName) {
                return (
                  <div>
                    <b>{row.original.challenge.name}</b>{" "}
                    {"/ " + t("components.MembersTable.project") + row.original.challenge.projectName}
                  </div>
                );
              }
              return <div>{row.original.challenge.name}</div>;

            default:
              return "NotImplemented";
          }
        },
      },

      {
        id: "options",
        Header: () => null,
        Cell: ({row}: MembersCell) => {
          switch (row.original.__typename) {
            case "EducationMemberRow":
              return (
                <span {...row.getToggleRowExpandedProps()}>
                  <MemberEditDropdown
                    member={row.original}
                    challenges={challenges}
                    projects={projects}
                    onAssign={onAssign}
                    onSetStatus={onSetStatus}
                    onUserBestAttemptsReportExport={onUserBestAttemptsReportExport}
                  />
                </span>
              );
            case "Participation":
              return (
                <span {...row.getToggleRowExpandedProps()}>
                  <ParticipationEditDropdown
                    participation={row.original}
                    onSetStatus={onSetParticipationStatus}
                    onParticipationDelete={onParticipationDelete}
                    onParticipationReportExport={onParticipationReportExport}
                  />
                </span>
              );
            default:
              return "NotImplemented";
          }
        },
      },

      {
        id: "hasActiveMembership",
        Header: () => (
          <div className={classes.withSort}>
            {t("components.MembersTable.status")} <SortButton field={EducationMemberSortField.ACTIVE} {...sortProps} />
          </div>
        ),
        Cell: ({row}: MembersCell) => {
          switch (row.original.__typename) {
            case "EducationMemberRow":
              return row.original.user.hasActiveMembership ? (
                <span className={cn(classes.status, classes.activeMember, classes.centerAlign)}></span>
              ) : (
                <span className={cn(classes.status, classes.notActiveMember, classes.centerAlign)}></span>
              );
            case "Participation":
              return !row.original.isActive ? (
                <div className={cn(classes.columnFlex)}>
                  <span className={cn(classes.notActiveParticipation)}>
                    {t("components.MembersTable.participationNotActive")}
                  </span>
                  {row.original.deactivatedAt && (
                    <span className={cn(classes.notActiveParticipation)}>
                      {formatToMoscowDate(row.original.deactivatedAt, i18n.language)}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  {row.original.activateAt || row.original.deactivateAt ? (
                    <div className={cn(classes.columnFlex)}>
                      <span className={cn(classes.notActiveParticipation, "whitespace-nowrap")}>
                        {row.original.activateAt && row.original.deactivateAt ? (
                          <>
                            {t("components.MembersTable.activeFrom")}{" "}
                            {formatToMoscowDate(row.original.activateAt, i18n.language)}
                            <br />
                            {t("components.MembersTable.activeTo").toLowerCase()}{" "}
                            {formatToMoscowDate(row.original.deactivateAt, i18n.language)}
                          </>
                        ) : row.original.activateAt ? (
                          <>
                            {t("components.MembersTable.activeSince")}{" "}
                            {formatToMoscowDate(row.original.activateAt, i18n.language)}
                          </>
                        ) : (
                          <>
                            {t("components.MembersTable.activeUntil")}{" "}
                            {formatToMoscowDate(row.original.deactivateAt, i18n.language)}
                          </>
                        )}
                      </span>
                    </div>
                  ) : null}
                </>
              );
            default:
              return "NotImplemented";
          }
        },
      },

      {
        id: "createdAt",
        Header: () => t("components.MembersTable.createdAt"),
        Cell: ({row}: MembersCell) => {
          let obj = row.original;

          if (singleSubRow && obj.__typename === "EducationMemberRow" && obj.participations.length === 1) {
            obj = obj.participations[0];
          }

          let time: number | null = null;

          switch (obj.__typename) {
            case "EducationMemberRow":
              return "";
            case "Participation":
              time = obj.createdAt;
              break;
            default:
              return "NotImplemented";
          }

          if (time === null) {
            return "";
          }

          return formatDateTime(time, i18n.language);
        },
      },

      {
        id: "startedAt",
        Header: () => (
          <div className={classes.withSort}>
            {t("components.MembersTable.startEducation")}
            <SortButton field={EducationMemberSortField.STARTED_AT} {...sortProps} />
          </div>
        ),
        Cell: ({row}: MembersCell) => {
          let obj = row.original;

          if (singleSubRow && obj.__typename === "EducationMemberRow" && obj.participations.length === 1) {
            obj = obj.participations[0];
          }

          let time: number | null | undefined = null;

          switch (obj.__typename) {
            case "EducationMemberRow":
            case "Participation":
              time = obj.startedAt;
              break;
            default:
              return "NotImplemented";
          }

          if (time === null || time === undefined) {
            return <i className={classes.notStarted}>{t("components.MembersTable.notStarted")}</i>;
          }

          return formatDateTime(time, i18n.language);
        },
      },

      {
        id: "attemptsCount",
        Header: () => (
          <div className={classes.withSort}>
            {t("components.MembersTable.attemptsCount")}
            <SortButton field={EducationMemberSortField.ATTEMPTS_COUNT} {...sortProps} />
          </div>
        ),
        Cell: ({row}: MembersCell) => {
          let obj = row.original;

          if (singleSubRow && obj.__typename === "EducationMemberRow" && obj.participations.length === 1) {
            obj = obj.participations[0];
          }

          let count: number | null = null;

          switch (obj.__typename) {
            case "EducationMemberRow":
              if (obj.attemptsCount) {
                return obj.attemptsCount;
              }
              break;
            case "Participation":
              if (obj.attemptsCount) {
                return (
                  <ReactLink
                    href={reverse("playerUserResults", {challengeId: obj.challenge.id, userId: obj.student.id})}
                  >
                    {obj.attemptsCount}
                  </ReactLink>
                );
              }
              break;
            default:
              return "NotImplemented";
          }

          return count ? count : 0;
        },
      },

      {
        id: "bestScore",
        Header: () => (
          <div className={classes.withSort}>
            {t("components.MembersTable.bestScore")}
            <SortButton field={EducationMemberSortField.AVERAGE_SCORE} {...sortProps} />
          </div>
        ),
        Cell: ({row}: MembersCell) => {
          let obj = row.original;

          if (singleSubRow && obj.__typename === "EducationMemberRow" && obj.participations.length === 1) {
            obj = obj.participations[0];
          }

          switch (obj.__typename) {
            case "EducationMemberRow":
              if (obj.averageScore) {
                return obj.averageScore + t("components.MembersTable.avg");
              }
              return "";
            case "Participation": {
              const mode = obj.challenge.mode;
              const isCompetenceOrRpgMode =
                mode === ChallengeMode.COMPETENCE_TEST || mode === ChallengeMode.RPG_MODE;

              if (isCompetenceOrRpgMode) {
                const results = obj.bestAttempt?.competenceResults;
                if (!results || results.length === 0) {
                  return (
                    <WithTooltip
                      as="span"
                      helpText={t("components.MembersTable.competenciesNotReady")}
                      className={classes.competenciesEmpty}
                    >
                      <span className={classes.competenciesCell}>
                        {t("components.MembersTable.competencies")}
                      </span>
                    </WithTooltip>
                  );
                }

                const tooltipText = results
                  .map(r => `${r.competence}: ${Math.round(r.score * 100)}%`)
                  .join("\n");

                const tooltipHtml = results
                  .map(r => `${r.competence}: ${Math.round(r.score * 100)}%`)
                  .join("<br>");

                return (
                  <span className={classes.competenciesCell}>
                    <WithTooltip
                      as="span"
                      helpText={tooltipHtml}
                      className={classes.competenciesLabel}
                    >
                      {t("components.MembersTable.competencies")}
                    </WithTooltip>
                    <button
                      title={t("components.MembersTable.copyCompetencies")}
                      onClick={(e) => {
                        navigator.clipboard.writeText(tooltipText);
                        const instance = tippy(e.currentTarget, {
                          content: t("components.CopyText.copied"),
                          trigger: "manual",
                          duration: 0,
                        });
                        instance.show();
                        setTimeout(() => instance.destroy(), 1500);
                      }}
                      className={classes.copyButton}
                    >
                      <CopyIcon />
                    </button>
                  </span>
                );
              }

              return obj.bestAttempt?.score ?? "";
            }
            default:
              return "[NotImplemented]";
          }
        },
      },

      {
        id: "bestTime",
        Header: () => t("components.MembersTable.bestTime"),
        Cell: ({row}: MembersCell) => {
          let obj = row.original;

          if (singleSubRow && obj.__typename === "EducationMemberRow" && obj.participations.length === 1) {
            obj = obj.participations[0];
          }

          switch (obj.__typename) {
            case "EducationMemberRow":
              return "";
            case "Participation":
              return obj.bestAttempt?.time ? formatDuration(obj.bestAttempt?.time) : "";
            default:
              return "[NotImplemented]";
          }
        },
      },
      {
        id: "efficiency",
        // accessor: 'efficiency' as keyof CellDataType,
        Header: () => {
          return (
            <div className={classes.withSort}>
              {t("components.MembersTable.efficiency")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.MembersTable.efficiencyTooltip")}
              >
                <HelpCircleIcon />
              </WithTooltip>
              <SortButton field={EducationMemberSortField.EFFICIENCY} {...sortProps} />
            </div>
          );
        },
        Cell: ({row}: MembersCell) => {
          let obj = row.original;
          let efficiency = 0;

          switch (obj.__typename) {
            case "EducationMemberRow":
              if (!obj.efficiency) {
                return "";
              }
              efficiency = parseFloat((obj.efficiency * 100).toFixed(1));
              return `${efficiency}%`;
            case "Participation":
              return "";
            default:
              return "[NotImplemented]";
          }
        },
      },
      getEmotionColumn(t),
      {
        id: "feedback",
        Header: () => (
          <div className={classes.withSort}>
            {t("components.MembersTable.feedback")}
            <SortButton field={EducationMemberSortField.AVERAGE_FEEDBACK_SCORE} {...sortProps} />
          </div>
        ),
        Cell: ({row}: MembersCell) => {
          let obj = row.original;
          let participationsWithText;
          if (obj.__typename === "EducationMemberRow") {
            participationsWithText = obj.participations.filter((p) => p.feedbackText);
          }

          if (singleSubRow && obj.__typename === "EducationMemberRow" && obj.participations.length === 1) {
            obj = obj.participations[0];
          }

          switch (obj.__typename) {
            case "EducationMemberRow":
              if (obj.averageFeedbackScore) {
                return (
                  <div>
                    <span>
                      {`${obj.averageFeedbackScore}/5 ${t("components.MembersTable.avg")}`}
                      <br />
                    </span>
                    {participationsWithText && participationsWithText.length >= 1 ? (
                      <div>
                        <WithExpandableText
                          text={participationsWithText[0].feedbackText ? participationsWithText[0].feedbackText : ""}
                          maxLength={20}
                        />
                        <br />
                      </div>
                    ) : null}
                    {participationsWithText && participationsWithText.length > 1 ? (
                      <span className={classes.andMore}>
                        {t("components.MembersTable.andMoreReviews", {count: participationsWithText.length - 1})}
                      </span>
                    ) : null}
                  </div>
                );
              }
              return "";
            case "Participation":
              return obj.feedbackScore || obj.feedbackText ? (
                <FeedbackWidget score={obj.feedbackScore} text={obj.feedbackText} />
              ) : null;
            default:
              return "NotImplemented";
          }
        },
      },

      {
        id: "record",
        Header: () => t("components.MembersTable.record"),
        Cell: ({row}: MembersCell) => {
          let obj = row.original;
          const [firstVideoVisible, showFirstVideo, hideFirstVideo] = useVisibility();
          const [bestVideoVisible, showBestVideo, hideBestVideo] = useVisibility();

          if (singleSubRow && obj.__typename === "EducationMemberRow" && obj.participations.length === 1) {
            obj = obj.participations[0];
          }

          switch (obj.__typename) {
            case "EducationMemberRow":
              return "";
            case "Participation":
              return (
                <div className={classes.records}>
                  {obj.firstAttempt && obj.firstAttempt.record && (
                    <>
                      <RecordLink
                        as={Link}
                        className={classes.recordButton}
                        data-id={obj.firstAttempt.record}
                        disabled={obj.firstAttempt.recordState !== EducationSessionRecordState.READY}
                        state={obj.firstAttempt.recordState}
                        onClick={showFirstVideo}
                        label={t("components.MembersTable.firstRecord")}
                        textVariant="short"
                      >
                        {obj.firstAttempt.recordState === EducationSessionRecordState.READY && (
                          <PlayIcon className={classes.playIcon} />
                        )}
                      </RecordLink>
                      {firstVideoVisible && (
                        <FullscreenVideo src={obj.firstAttempt.record ?? undefined} onClose={hideFirstVideo} />
                      )}
                    </>
                  )}
                  {obj.bestAttempt &&
                    obj.bestAttempt.record &&
                    obj.firstAttempt?.record !== obj.bestAttempt.record && (
                      <>
                        <RecordLink
                          as={Link}
                          className={classes.recordButton}
                          data-id={obj.bestAttempt.record}
                          disabled={obj.bestAttempt.recordState !== EducationSessionRecordState.READY}
                          state={obj.bestAttempt.recordState}
                          onClick={showBestVideo}
                          label={t("components.MembersTable.bestRecord")}
                          textVariant="short"
                        >
                          {obj.bestAttempt.recordState === EducationSessionRecordState.READY && (
                            <PlayIcon className={classes.playIcon} />
                          )}
                        </RecordLink>
                        {bestVideoVisible && (
                          <FullscreenVideo src={obj.bestAttempt.record ?? undefined} onClose={hideBestVideo} />
                        )}
                      </>
                    )}
                </div>
              );
            default:
              return "NotImplemented";
          }
        },
      },
    ];
  }, [
    t,
    i18n,
    challenges,
    projects,
    onAssign,
    onSetStatus,
    onSetParticipationStatus,
    onParticipationDelete,
    onUserBestAttemptsReportExport,
    onParticipationReportExport,
    singleSubRow,
    defaultSort,
    onSetSort,
    sort,
  ]);

  return useTable<CellDataType>(
    {
      columns: columns,
      data: data,

      getSubRows: (row) => {
        if (row.__typename === "EducationMemberRow" && !singleSubRow) {
          return row.participations;
        }

        return [];
      },
    },
    useExpanded,
  );
}

const MembersTableTable = {
  use: useMembersTableTable,
  fragments: {
    root: gql`
      fragment MembersTable on EducationMemberRow {
        id

        ...MemberEditDropdown
        efficiency
        participations {
          id

          challenge {
            id
            name
            projectName
            mode
          }
          student {
            id
            fullName
            email
          }
          createdAt
          startedAt
          activateAt
          deactivateAt
          deactivatedAt
          isActive
          attemptsCount
          firstAttempt {
            record
            recordState
          }
          bestAttempt {
            time
            score
            record
            recordState
            competenceResults {
              competence
              score
            }
          }
          feedbackScore
          feedbackText
        }
        user {
          id
          ...UserItem
        }
        averageScore
        averageFeedbackScore
        attemptsCount
        startedAt
      }
      ${MemberEditDropdown.fragments.root}
      ${UserItem.fragments.root}
    `,
    challenges: gql`
      fragment MembersTableChallenges on Challenge {
        ...MemberEditDropdownChallenges
      }
      ${MemberEditDropdown.fragments.challenges}
    `,
    projects: gql`
      fragment MembersTableProjects on Project {
        ...MemberEditDropdownProjects
      }
      ${MemberEditDropdown.fragments.projects}
    `,
  },
};

export default MembersTableTable;
